import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  Button,
  Divider,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { loadStripe } from "@stripe/stripe-js";

const ShoppingCart = () => {
  const location = useLocation();
  const [cart, setCart] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [shipping, setShipping] = useState(8);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponError, setCouponError] = useState('');

  // ✅ Helper function to ensure number
  const toNumber = (value, defaultValue = 0) => {
    const num = parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Fetch cart from backend on mount
  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cart');
      const data = await response.json();
      
      if (data.success) {
        setCart(data.cart || []);
        setTotalQty(toNumber(data.totalQty, 0));
        setTotalPrice(toNumber(data.totalPrice, 0));
        setTotalAmount(toNumber(data.totalAmount, 0));
        setShipping(toNumber(data.shipping, 8));
        
        // Sync to localStorage
        localStorage.setItem('cart', JSON.stringify(data.cart));
      }
    } catch (err) {
      console.error("Error fetching cart:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateCartState = (data) => {
    if (data.success) {
      const products = data.products || [];
      setCart(products);
      setTotalQty(toNumber(data.totalQty || data.cartQty, 0));
      setTotalPrice(toNumber(data.totalPrice, 0));
      setTotalAmount(toNumber(data.totalAmount || data.totalPrice, 0));
      setShipping(toNumber(data.shipping, 8));
      
      // Sync to localStorage
      localStorage.setItem('cart', JSON.stringify(products));
    }
  };

  const handleIncrease = async (item) => {
    try {
      console.log("Increasing item:", item);
      const itemId = item.id || item._id;
      const res = await fetch(`/api/increase/${itemId}`);
      const data = await res.json();
      
      if (data.success) {
        console.log("🆙 Increased item:", data);
        updateCartState(data);
      } else {
        console.error("Failed to increase item:", data.error);
      }
    } catch (err) {
      console.error("Error increasing item:", err);
    }
  };

  const handleReduce = async (item) => {
    try {
      console.log("Reducing item:", item);
      const itemId = item.id || item._id;
      const res = await fetch(`/api/reduce/${itemId}`);
      const data = await res.json();
      
      if (data.success) {
        console.log("🔽 Reduced item:", data);
        updateCartState(data);
      } else {
        console.error("Failed to reduce item:", data.error);
      }
    } catch (err) {
      console.error("Error reducing item:", err);
    }
  };

  const handleRemove = async (item) => {
    try {
      console.log("Removing item:", item);
      const itemId = item.id || item._id;
      const res = await fetch(`/api/remove/${itemId}`);
      const data = await res.json();
      
      if (data.success) {
        console.log("🗑 Removed item:", data);
        updateCartState(data);
      } else {
        console.error("Failed to remove item:", data.error);
      }
    } catch (err) {
      console.error("Error removing item:", err);
    }
  };

  const handleApplyCoupon = async () => {
    setCouponMessage('');
    setCouponError('');
    
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    try {
      const res = await fetch('/api/cart/apply-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ couponCode: couponCode.toUpperCase() })
      });
      
      const data = await res.json();
      
      if (data.success) {
        setCouponMessage(data.message);
        setTotalAmount(toNumber(data.totalAmount, 0));
        setShipping(toNumber(data.shipping, 0));
        setCouponCode('');
        
        // Refresh cart to get updated values
        await fetchCart();
      } else {
        setCouponError(data.message || 'Invalid coupon code');
      }
    } catch (err) {
      console.error("Error applying coupon:", err);
      setCouponError('Failed to apply coupon');
    }
  };

  const makePayment = async () => {
    try {
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      
      console.log("Sending cart to Stripe:", cart);
      
      const body = { products: cart };
      const headers = { "Content-Type": "application/json" };
          console.log("Stripe Cart : "+JSON.stringify(body))

      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: headers,
        body: JSON.stringify(body),
      });

      
      const session = await response.json();
      
      if (session.url) {
        window.location.href = session.url;
      } else {
        alert("Payment initialization failed");
      }
    } catch (err) {
      console.error("Stripe error:", err);
      alert("Could not start payment");
    }
  };


  const navLinks = [
    { text: "TOUR", link: "/#upcoming" },
    { text: "STREAM", link: "/#stream" },
    { text: "RELEASES", link: "/#releases" },
    { text: "SHOP", link: "/#boutique", external: true },
    { text: "ABOUT", link: "/#about" },
  ];

  if (loading) {
    return (
      <>
        <Header menuItems={navLinks} cartQty={totalQty} />
        <Box sx={{ 
          minHeight: "100vh", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          backgroundImage: "url(/images/rainbow_bg.jpg)",
          backgroundSize: "cover"
        }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Header menuItems={navLinks} cartQty={totalQty} />
      <Box
        sx={{
          backgroundImage: "url(/images/rainbow_bg.jpg)",
          backgroundRepeat: "no-repeat",
          backgroundSize: "100% 100%",
          backgroundAttachment: "fixed",
          backgroundPosition: "center bottom",
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container sx={{ bgcolor: "white", opacity: 0.95, p: 3, borderRadius: 2 }}>
          {cart.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ShoppingCartIcon sx={{ mr: 1, fontSize: 30 }} />
                <Typography variant="h5">
                  <strong>Shopping Cart ({totalQty} items)</strong>
                </Typography>
              </Box>

              <List>
                {cart.map((product, idx) => {
                  const itemPrice = toNumber(product.price, 0);
                  const itemQty = toNumber(product.qty, 1);
                  console.log("Item price: ",itemPrice);
                  return (
                    <React.Fragment key={idx}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Box sx={{ position: "relative", width: 120, height: 120 }}>
                            <Avatar
                              variant="square"
                              src={product.item?.imagePath}
                              sx={{ width: "100%", height: "100%" }}
                            />
                            {product.item?.optionPath && (
                              <Avatar
                                variant="square"
                                src={product.item.optionPath}
                                sx={{
                                  width: "50%",
                                  height: "50%",
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                }}
                              />
                            )}
                          </Box>
                        </ListItemAvatar>
                        
                        <ListItemText
                          sx={{ ml: 2 }}
                          primary={
                            <Typography variant="h6">
                              <strong>{product.item?.title || product.item?.description || "Item"}</strong>
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {product.item?.description}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Unit Price: ${toNumber(product.item?.price, 0).toFixed(2)}
                              </Typography>
                              
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Button 
                                    size="small" 
                                    variant="outlined"
                                    onClick={() => handleReduce(product.item)}
                                    disabled={itemQty <= 1}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </Button>
                                  
                                  <Badge 
                                    badgeContent={itemQty} 
                                    color="primary" 
                                    sx={{ 
                                      '& .MuiBadge-badge': { 
                                        fontSize: '1rem',
                                        height: '28px',
                                        minWidth: '28px',
                                        borderRadius: '14px'
                                      } 
                                    }}
                                  >
                                    <Box sx={{ width: 24, height: 24 }} />
                                  </Badge>
                                  
                                  <Button 
                                    size="small" 
                                    variant="outlined"
                                    onClick={() => handleIncrease(product.item)}
                                  >
                                    <AddIcon fontSize="small" />
                                  </Button>
                                </Box>
                                
                                <Typography variant="h6" color="success.main" sx={{ ml: 2 }}>
                                  ${itemPrice.toFixed(2)}
                                </Typography>
                                
                                <Button 
                                  size="small" 
                                  color="error"
                                  onClick={() => handleRemove(product.item)}
                                  sx={{ ml: 'auto' }}
                                >
                                  <DeleteIcon /> Remove
                                </Button>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {idx < cart.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Coupon Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Have a coupon code?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button 
                    variant="outlined" 
                    onClick={handleApplyCoupon}
                    disabled={!couponCode.trim()}
                  >
                    Apply
                  </Button>
                </Box>
                {couponMessage && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {couponMessage}
                  </Alert>
                )}
                {couponError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {couponError}
                  </Alert>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Cart Summary */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1">
                    ${toNumber(totalPrice, 0).toFixed(2)}
                  </Typography>
                </Box>
                
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body1">Shipping:</Typography>
                  <Typography variant="body1">
                    {toNumber(shipping, 0) === 0 ? (
                      <span style={{ color: 'green', fontWeight: 'bold' }}>FREE</span>
                    ) : (
                      `$${toNumber(shipping, 0).toFixed(2)}`
                    )}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography variant="h6">
                    <strong>Total:</strong>
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    <strong>${toNumber(totalAmount, 0).toFixed(2)}</strong>
                  </Typography>
                </Box>

                <Button 
                  fullWidth
                  variant="contained" 
                  color="success" 
                  size="large"
                  onClick={makePayment}
                  sx={{ py: 1.5 }}
                >
                  Proceed to Checkout with Stripe
                </Button>
              </Box>

              {/* Additional Info */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  * Secure checkout powered by Stripe
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShoppingCartIcon sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
              <Typography variant="h4" color="text.secondary" gutterBottom>
                <strong>Your cart is empty</strong>
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Start shopping to add items to your cart
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => window.location.href = '/#boutique'}
                sx={{ mt: 2 }}
              >
                Browse Products
              </Button>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default ShoppingCart;
